import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import '../styles/Log.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const REFRESH_INTERVAL_MS = 15000;

const TABS = [
  { key: 'buertha', label: 'Buertha' },
  { key: 'django', label: 'Django' },
  { key: 'celery', label: 'Celery' },
  { key: 'os', label: 'OS-level' },
];

function Log({ user }) {
  const [activeTab, setActiveTab] = useState('buertha');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const logBottomRef = useRef(null);

  const canView = (user?.roles || []).includes('admin');

  const fetchLogs = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'buertha') {
        const params = {};
        if (level) params.level = level;
        if (search) params.search = search;
        const res = await axios.get(`${API_BASE_URL}/logs/app/`, { params });
        setEntries(res.data);
        setLines([]);
        setMeta(null);
      } else if (activeTab === 'django' || activeTab === 'celery') {
        const name = activeTab === 'celery' ? 'celery' : 'django';
        const res = await axios.get(`${API_BASE_URL}/logs/file/${name}/`);
        setLines(res.data.lines || []);
        setMeta({ paths: res.data.paths });
        setEntries([]);
      } else if (activeTab === 'os') {
        const res = await axios.get(`${API_BASE_URL}/logs/os/`);
        setLines(res.data.lines || []);
        setMeta({ source: res.data.source, warning: res.data.warning });
        setEntries([]);
        if (res.data.error) setError(res.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load logs');
      setEntries([]);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, level, search, canView]);

  useEffect(() => {
    fetchLogs();
    const timerId = setInterval(fetchLogs, REFRESH_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [fetchLogs]);

  useEffect(() => {
    logBottomRef.current?.scrollIntoView({ block: 'nearest' });
  }, [entries, lines]);

  if (!canView) {
    return (
      <div className="log-page">
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="log-page">
      <h2>Logs</h2>
      <p className="log-intro">View application, service, and OS-level logs.</p>

      <div className="log-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`log-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="log-controls">
        {activeTab === 'buertha' && (
          <>
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="log-level-select">
              <option value="">All levels</option>
              <option value="DEBUG">Debug</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
              <option value="CRITICAL">Critical</option>
            </select>
            <input
              type="text"
              placeholder="Search message or logger..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="log-search-input"
            />
          </>
        )}
        <button className="log-refresh-btn" onClick={fetchLogs} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {meta?.paths && <p className="log-meta">File: {meta.paths.join(', ')}</p>}
      {meta?.source && <p className="log-meta">Source: {meta.source}</p>}
      {meta?.warning && <p className="log-meta log-warning">{meta.warning}</p>}
      {error && <p className="log-error">{error}</p>}

      {activeTab === 'buertha' ? (
        <div className="log-entries">
          {entries.length === 0 && !loading && <p className="log-empty">No log entries yet.</p>}
          {entries.map((entry) => (
            <div key={entry.id} className={`log-entry log-level-${entry.level.toLowerCase()}`}>
              <span className="log-entry-time">{new Date(entry.timestamp).toLocaleString()}</span>
              <span className="log-entry-level">{entry.level}</span>
              <span className="log-entry-logger">{entry.logger_name}</span>
              <span className="log-entry-message">{entry.message}</span>
            </div>
          ))}
          <div ref={logBottomRef} />
        </div>
      ) : (
        <pre className="log-file-view">
          {lines.length === 0 && !loading && !error ? 'No log output yet.' : lines.join('')}
          <div ref={logBottomRef} />
        </pre>
      )}
    </div>
  );
}

export default Log;
